import { FormEvent, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { fetchJSON, getWpBaseUrl } from "../../utils/api";
import type { WPPage } from "../../content/contentClient";
import type { LanguageCode } from "../../hooks/useLanguage";

type ContactFormProps = {
  lang: LanguageCode;
  page?: WPPage | null;
};

type ContactResponse = {
  message?: string;
};

type FormState = {
  name: string;
  email: string;
  phone: string;
  message: string;
  consent: boolean;
  company: string;
};

const DEFAULT_STATE: FormState = {
  name: "",
  email: "",
  phone: "",
  message: "",
  consent: false,
  company: ""
};

const LIMITS = {
  name: 120,
  email: 190,
  phone: 40,
  messageMin: 12,
  messageMax: 4000
} as const;

const CONTACT_PAGE_SLUGS = new Set(["kontakt", "contact"]);

const TEXT = {
  de: {
    title: "Kontakt",
    intro: "Schreib mir eine Nachricht. Ich melde mich so bald wie moeglich zurueck.",
    name: "Name",
    email: "E-Mail",
    phone: "Telefon (optional)",
    message: "Nachricht",
    consent: "Ich stimme der Verarbeitung meiner Angaben zur Beantwortung meiner Anfrage zu.",
    privacyHint: "Die Angaben werden ausschliesslich zur Bearbeitung der Anfrage verwendet.",
    submit: "Nachricht senden",
    success: "Vielen Dank. Deine Nachricht wurde erfolgreich versendet.",
    error: "Die Nachricht konnte gerade nicht versendet werden. Bitte spaeter erneut versuchen.",
    required: "Bitte fuelle alle Pflichtfelder aus.",
    invalidEmail: "Bitte gib eine gueltige E-Mail-Adresse ein.",
    messageTooShort: "Bitte schreibe eine etwas ausfuehrlichere Nachricht."
  },
  en: {
    title: "Contact",
    intro: "Send me a message. I will get back to you as soon as possible.",
    name: "Name",
    email: "Email",
    phone: "Phone (optional)",
    message: "Message",
    consent: "I agree to the processing of my data for answering my enquiry.",
    privacyHint: "Your data is used exclusively to process your enquiry.",
    submit: "Send message",
    success: "Thank you. Your message has been sent successfully.",
    error: "Your message could not be sent right now. Please try again later.",
    required: "Please complete all required fields.",
    invalidEmail: "Please enter a valid email address.",
    messageTooShort: "Please write a slightly more detailed message."
  }
} as const;

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const isContactPage = (page?: WPPage | null) => {
  const slug = page?.link ? new URL(page.link).pathname.split("/").filter(Boolean).pop() : "";
  const pageSlug = slug || "";
  if (CONTACT_PAGE_SLUGS.has(pageSlug)) return true;
  return page?.acf?.contact_form_enabled === true && Boolean(page?.acf?.contact_recipient_email);
};

export default function ContactForm({ lang, page }: ContactFormProps) {
  const [form, setForm] = useState<FormState>(DEFAULT_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [startedAt] = useState(() => Date.now());

  const text = TEXT[lang];
  const acf = page?.acf || {};
  const title = acf.contact_form_title || text.title;
  const intro = acf.contact_form_intro || text.intro;
  const submitLabel = acf.contact_submit_label || text.submit;
  const successMessage = acf.contact_success_message || text.success;
  const privacyHint = acf.contact_privacy_hint || text.privacyHint;

  const updateField = (key: keyof FormState, value: string | boolean) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!form.name.trim() || !form.email.trim() || !form.message.trim() || !form.consent) {
      setError(text.required);
      return;
    }

    if (!isValidEmail(form.email)) {
      setError(text.invalidEmail);
      return;
    }

    if (form.message.trim().length < LIMITS.messageMin) {
      setError(text.messageTooShort);
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        consent: form.consent,
        company: form.company,
        startedAt,
        pageSlug: page?.link ? new URL(page.link).pathname.split("/").filter(Boolean).pop() : "",
        pageTitle: page?.title?.rendered || "",
        pageLanguage: lang
      };

      const response = await fetchJSON<ContactResponse>("/multisite/v1/contact", {
        baseUrl: getWpBaseUrl(),
        method: "POST",
        body: payload
      });

      setSuccess(response?.message || successMessage);
      setForm(DEFAULT_STATE);
    } catch {
      setError(text.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="section"
      sx={{
        mt: 5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        p: { xs: 2.5, md: 3.5 }
      }}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h2" sx={{ fontSize: { xs: "1.6rem", md: "2rem" } }}>
            {title}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {intro}
          </Typography>
        </Box>

        {success ? <Alert severity="success">{success}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label={text.name}
              value={form.name}
              onChange={(event) => updateField("name", event.target.value)}
              required
              inputProps={{ maxLength: LIMITS.name }}
              fullWidth
            />
            <TextField
              label={text.email}
              type="email"
              value={form.email}
              onChange={(event) => updateField("email", event.target.value)}
              required
              inputProps={{ maxLength: LIMITS.email }}
              fullWidth
            />
            <TextField
              label={text.phone}
              value={form.phone}
              onChange={(event) => updateField("phone", event.target.value)}
              inputProps={{ maxLength: LIMITS.phone }}
              fullWidth
            />
            <TextField
              label={text.message}
              value={form.message}
              onChange={(event) => updateField("message", event.target.value)}
              required
              fullWidth
              multiline
              minRows={6}
              inputProps={{ maxLength: LIMITS.messageMax }}
            />
            <Box sx={{ position: "absolute", left: -10000, top: "auto", width: 1, height: 1, overflow: "hidden" }}>
              <TextField
                tabIndex={-1}
                autoComplete="new-password"
                label="Company"
                value={form.company}
                onChange={(event) => updateField("company", event.target.value)}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.consent}
                  onChange={(event) => updateField("consent", event.target.checked)}
                />
              }
              label={text.consent}
            />
            <Typography variant="body2" color="text.secondary">
              {privacyHint}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitLabel}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
